import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ERROR_CODES, ErrCode } from '../models';
import { TurnstileService } from './turnstile.service';
import { TurnstileErrorData, TurnstileRefreshExpired, TurnstileRenderOpts, TurnstileSize } from './turnstile.types';

@Component({
  selector: 'app-turnstile',
  templateUrl: './turnstile.component.html',
  styleUrls: ['./turnstile.component.scss'],
})
export class TurnstileComponent implements OnInit, OnDestroy {
  @Input() public action!: string;
  @Input() public size: TurnstileSize = 'normal';
  /** Cloudflare's default `auto` re-solves every ~300s and emits each token through `tokenReceived`. */
  @Input() public refreshExpired: TurnstileRefreshExpired = 'auto';
  /** Dev-only inline error. Off where the host already renders its own failure message. */
  @Input() public showInlineError = true;

  @Output() public readonly tokenReceived = new EventEmitter<string>();
  @Output() public readonly loadError = new EventEmitter<ErrCode>();
  @Output() public readonly expired = new EventEmitter<void>();
  /**
   * Cloudflare is about to ask the visitor to act. Only of interest to a host that keeps the
   * widget hidden between challenges and has to surface it; not a general readiness signal.
   */
  @Output() public readonly interactive = new EventEmitter<void>();
  @Output() public readonly widgetReset = new EventEmitter<void>();

  @ViewChild('host', { static: true })
  public hostRef!: ElementRef<HTMLDivElement>;

  public loadErrCode: ErrCode | undefined;
  private widgetId: string | undefined;

  constructor(private turnstileService: TurnstileService) {}

  public get isProduction(): boolean {
    return Boolean(environment.production);
  }

  public ngOnInit(): void {
    if (!this.turnstileService.isEnabled()) {
      return;
    }
    const renderOpts: TurnstileRenderOpts = {
      sitekey: this.turnstileService.getSiteKey(),
      action: this.action,
      theme: 'light',
      size: this.size,
      'refresh-expired': this.refreshExpired,
      callback: (token: string) => this.tokenReceived.emit(token),
    };
    renderOpts['error-callback'] = (errorCode: string): void => {
      const data: TurnstileErrorData = { turnstileErrorCode: errorCode };
      const err = new ErrCode(ERROR_CODES.TURNSTILE_ERROR, `Turnstile error: ${errorCode}`, data);
      this.loadErrCode = err;
      this.loadError.emit(err);
    };
    renderOpts['expired-callback'] = (): void => this.expired.emit();
    renderOpts['before-interactive-callback'] = (): void => this.interactive.emit();
    this.turnstileService
      .render(this.hostRef.nativeElement, renderOpts)
      .then((id) => {
        this.widgetId = id;
      })
      .catch((err: ErrCode) => {
        this.loadErrCode = err;
        this.loadError.emit(err);
      });
  }

  public ngOnDestroy(): void {
    if (this.widgetId) {
      this.turnstileService.remove(this.widgetId);
      this.widgetId = undefined;
    }
  }

  public resetWidget(): void {
    if (this.widgetId) {
      this.turnstileService.reset(this.widgetId);
      this.widgetReset.emit();
    }
  }
}
